import { useEffect, useState } from 'react'

/**
 * 포인트가 켜져 있는지는 **서버에게만 묻는다** (`GET /api/coins`).
 * 스위치는 서버의 `COINS_ENABLED` 한 곳이다.
 *
 * 서버가 답하지 않으면 **숨긴다.** 잠금은 어차피 서버가 지키므로
 * (AI 를 부르는 길목마다 402), 화면이 숨겨서 손해 보는 사람은 없다.
 */

let cached: Promise<boolean> | null = null

/** 한 번만 묻고 그 답을 계속 쓴다. 탭을 새로 고치면 다시 묻는다. */
export function loadCoinsEnabled(): Promise<boolean> {
  cached ??= fetch('/api/coins')
    .then((res) => (res.ok ? (res.json() as Promise<{ enabled?: boolean }>) : null))
    .then((body) => body?.enabled === true)
    .catch(() => false)
  return cached
}

/** 검사에서 쓰는 되돌리기 (화면 코드에서는 부르지 않는다) */
export function resetCoinsEnabledCache(): void {
  cached = null
}

export function useCoinsEnabled(): boolean {
  return useCoinsSwitch().on
}

/**
 * **아직 모르는 동안을 가른다**.
 *
 * `useCoinsEnabled()` 는 답이 오기 전에 `false` 다 — "꺼져 있다" 와 "아직 모른다" 가
 * 같은 값이었다. AI 를 부르는 상품은 서버가 막으니 그래도 됐지만, **부적은 화면에서
 * 그린다.** 그래서 답이 오기 전 한 순간 잠금이 풀린 것으로 보여 그림이 그려졌고,
 * 그 틈에 칸(canvas)을 꺼내면 값을 내지 않고 가져갈 수 있었다 (화면 검사가 그 틈을 잡았다).
 *
 * 그리는 쪽은 `ready` 가 될 때까지 잠근다.
 */
export function useCoinsSwitch(): { on: boolean; ready: boolean } {
  const [state, setState] = useState<{ on: boolean; ready: boolean }>({ on: false, ready: false })
  useEffect(() => {
    let cancelled = false
    void loadCoinsEnabled().then((v) => !cancelled && setState({ on: v, ready: true }))
    return () => {
      cancelled = true
    }
  }, [])
  return state
}
